<template>
    <div>
        <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
        <div v-else-if="showNoData" class="no-result">
            <div class="no-result-container">
                <h3>
                    {{ hasMultipleRecords ? "Multiple Parcel Ids Found For Assessor" : "No data available for Assessor"
                    }}
                </h3>
                <template v-if="proofDocuments && proofDocuments.length">
                    <h4>Supporting Documents</h4>
                    <div class="screenshot-docs-container">
                        <div v-for="docValue in proofDocuments" :key="docValue.name" class="screenshot-doc">
                            <h5>{{ docValue.name }}</h5>
                            <div class="action-column">
                                <v-btn
                                    color="primary"
                                    class="navigation-btn"
                                    v-if="docValue.site_url"
                                    @click="handleAction(docValue,'open-site')"
                                    >Link to website</v-btn
                                >
                                <v-btn color="primary" class="navigation-btn" @click="handleAction(docValue)"
                                    >View file</v-btn
                                >
                                <v-btn color="primary" class="navigation-btn" @click="handleAction(docValue,'download')"
                                    >Download file</v-btn
                                >
                            </div>
                        </div>
                    </div>
                </template>
            </div>
            <div v-if="hasMultipleRecords">
                <v-tabs v-model="activeTab">
                    <v-tab href="#multiple-parcels">Multiple Parcels Found</v-tab>
                    <v-tab href="#single-parcel">Retrieved Parcel</v-tab>
                </v-tabs>
                <v-tabs-items v-model="activeTab" class="tabs-container">
                    <v-tab-item value="multiple-parcels">
                        <div class="multiple-parcels-container">
                            <customTable :headers="multipleParcelsTableHeaders" :items="multipleParcelsData">
                                <template #item.action="{ item }">
                                    <v-tooltip v-if="!item.is_selected" top :disabled="!tooltipText">
                                        <template v-slot:activator="{ on, attrs }">
                                            <div v-on="on" class="d-inline-block">
                                                <v-btn
                                                    class="navigation-btn"
                                                    :disabled="!allowRetrieval"
                                                    @click="handleRetrieveParcel(item)"
                                                    >Retrieve Parcel</v-btn
                                                >
                                            </div>
                                        </template>
                                        <span>{{ tooltipText }}</span>
                                    </v-tooltip>
                                    <span v-else class="retrieved-label">Retrieved</span>
                                </template>
                            </customTable>
                        </div>
                    </v-tab-item>
                    <v-tab-item value="single-parcel">
                        <div>
                            <div class="container">
                                <showDetails
                                    v-if="taxData"
                                    :data="taxData"
                                    :highlight-keys="['additional_information']"
                                />
                            </div>
                            <div class="card" v-if="tableData.length">
                                <div class="card-header">
                                    <h2>Documents</h2>
                                </div>
                                <v-data-table
                                    :headers="headers"
                                    :items="tableData"
                                    item-key="id"
                                    :server-items-length="tableData.length"
                                    :loading="loading"
                                    loading-text="Loading... Please wait"
                                    fixed-header
                                    :hide-default-footer="true"
                                    :hide-default-header="true"
                                    class="custom-v-table"
                                >
                                    <template #header="{ props }">
                                        <tr class="custom-header-row">
                                            <th v-for="header in props.headers" :key="header.value">
                                                {{ header.text }}
                                            </th>
                                        </tr>
                                    </template>
                                    <template #item="{ item }">
                                        <tr class="custom-data-row">
                                            <td>{{ item.content_location}}</td>
                                            <td>{{ item.site_type }}</td>
                                            <td>
                                                <div class="action-column">
                                                    <v-btn
                                                        color="primary"
                                                        class="navigation-btn"
                                                        v-if="item.site_url"
                                                        @click="handleAction({site_url:item.json_data.Site_url},'open-site')"
                                                        >Link to website</v-btn
                                                    >
                                                    <v-btn
                                                        color="primary"
                                                        class="navigation-btn"
                                                        @click="handleAction(item)"
                                                        >View file</v-btn
                                                    >
                                                    <v-btn
                                                        color="primary"
                                                        class="navigation-btn"
                                                        @click="handleAction(item,'download')"
                                                        >Download file</v-btn
                                                    >
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                </v-data-table>
                            </div>
                            <additionalRunSheetTable v-if="additionalTables.length > 0" :tables="additionalTables" />
                        </div>
                    </v-tab-item>
                </v-tabs-items>
            </div>
        </div>
        <div v-else>
            <div class="container">
                <showDetails v-if="taxData" :data="taxData" :highlight-keys="['additional_information']" />
            </div>
            <div class="card" v-if="tableData.length">
                <div class="card-header">
                    <h2>Documents</h2>
                </div>
                <v-data-table
                    :headers="headers"
                    :items="tableData"
                    item-key="id"
                    :server-items-length="tableData.length"
                    :loading="loading"
                    loading-text="Loading... Please wait"
                    fixed-header
                    :hide-default-footer="true"
                    :hide-default-header="true"
                    class="custom-v-table"
                >
                    <template #header="{ props }">
                        <tr class="custom-header-row">
                            <th v-for="header in props.headers" :key="header.value">{{ header.text }}</th>
                        </tr>
                    </template>
                    <template #item="{ item }">
                        <tr class="custom-data-row">
                            <td>{{ item.content_location}}</td>
                            <td>{{ item.site_type }}</td>
                            <td>
                                <div class="action-column">
                                    <v-btn
                                        color="primary"
                                        class="navigation-btn"
                                        v-if="item.json_data.Site_url"
                                        @click="handleAction({site_url:item.json_data.Site_url},'open-site')"
                                        >Link to website</v-btn
                                    >
                                    <v-btn color="primary" class="navigation-btn" @click="handleAction(item)"
                                        >View file</v-btn
                                    >
                                    <v-btn color="primary" class="navigation-btn" @click="handleAction(item,'download')"
                                        >Download file</v-btn
                                    >
                                </div>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </div>
            <additionalRunSheetTable v-if="additionalTables.length > 0" :tables="additionalTables" />
        </div>
        <filePreviewModal :dialog="dialog" :data="selectedData" @close="dialog = false" />
    </div>
</template>

<script>
import { GETSCRAPPEDRUNSHEETDATA, TRIGGERRUNSHEETSCRAPPING } from '@/store/actions.type'
import filePreviewModal from '@/components/filePreviewModal.vue'
import { IMPORTANT_ORDER_DETAILS_KEYS, SITE_TYPE, SCRAPPER_STATES } from '@/constants'
import additionalRunSheetTable from './commonComponents/additionalRunSheetTable.vue'
import customTable from './commonComponents/customTable.vue'
import showDetails from './commonComponents/showDetails.vue'
import { formatKeyToLabel } from '../utils'
import { ASSESSOR_TABLE_COLUMNS_ORDER } from '../constants'

export default {
  name: 'RunSheetAccessorTab',
  components: {
    filePreviewModal,
    additionalRunSheetTable,
    showDetails,
    customTable
  },
  data () {
    return {
      scrappedData: [],
      taxData: null,
      tableData: [],
      showNoData: false,
      headers: [
        { text: 'Location', value: 'content_location', sortable: false },
        { text: 'Site Type', value: 'site_type', sortable: false },
        { text: 'Action', value: 'action', sortable: false }
      ],
      multipleParcelsTableHeaders: [],
      multipleParcelsData: [],
      options: {},
      loading: false,
      dialog: false,
      selectedData: null,
      importantOrderDetails: IMPORTANT_ORDER_DETAILS_KEYS,
      additionalTables: [],
      proofDocuments: [],
      hasMultipleRecords: false,
      multipleParcelCaseDocNames: ['address', 'multiple_record_found'],
      activeTab: null
    }
  },
  props: {
    order: null,
    fetchOnActive: {
      type: Boolean,
      default: false
    },
    tooltipText: {
      type: String,
      default: ''
    },
    scrapperState: {
      type: Object,
      default: () => ({})
    },
    refetchScrapperState: {
      type: Function,
      default: null
    }
  },
  mounted () {
    this.getData()
  },
  methods: {
    getData () {
      this.loading = true
      this.$store.dispatch(GETSCRAPPEDRUNSHEETDATA, {
        orderId: this.order.id,
        siteType: SITE_TYPE.ASSSESSOR
      }).then(response => {
        this.scrappedData = response.results
        this.filterAndSetData()
      }).finally(() => {
        this.loading = false
      })
    },
    filterAndSetData () {
      if (this.scrappedData) {
        const data = this.scrappedData.find(item => item.data_type === 'json')
        if (data && data.json_data) {
          const updatedData = data.json_data
          this.taxData = updatedData.current_parcel || null
          if (updatedData.current_parcel) {
            const tableData = updatedData.current_parcel.additional_tables
            this.additionalTables = tableData || []
          }
        }
        if (this.scrappedData.length > 0) {
          const tableData = this.scrappedData.filter(item => {
            const { content_location: name, data_type: docType } = item
            return docType !== 'json' && !this.multipleParcelCaseDocNames.includes(name)
          })
          this.tableData = tableData
        }
      }
    },
    handleAction (item, mode = 'view') {
      if (mode === 'view') {
        this.selectedData = item
        this.dialog = true
      } else if (mode === 'open-site') {
        const { site_url: url } = item
        url && window.open(url, '_blank')
      } else {
        const { file_path: fileLink } = item
        window.open(fileLink, '_blank')
      }
    },
    updateNoDataState () {
      const scrappedDataJson = this.scrappedData ? this.scrappedData.find(item => item.data_type === 'json') : null

      if (this.scrappedData && this.scrappedData.length > 0) {
        const proofDocsData = this.scrappedData.filter(item => item.data_type === 'pdf')

        if (proofDocsData && proofDocsData.length > 0) {
          this.proofDocuments = proofDocsData.map(item => {
            const { Site_url: siteUrl } = item.json_data || {}

            return {
              ...item,
              name: formatKeyToLabel(item.content_location || 'document'),
              site_url: siteUrl || null
            }
          })
        }
      }
      if (scrappedDataJson && scrappedDataJson.json_data) {
        const jsonData = scrappedDataJson.json_data

        if (jsonData.all_parcels && jsonData.all_parcels.length > 1) {
          this.hasMultipleRecords = true
          this.multipleParcelsData = jsonData.all_parcels

          const data = jsonData.all_parcels[0] || {}
          const headers = Object.keys(data).filter(
            (key) => {
              const keysToExclude = ['is_selected']
              return !keysToExclude.includes(key)
            }
          ).sort((a, b) => {
            return (ASSESSOR_TABLE_COLUMNS_ORDER[a] || 1000) - (ASSESSOR_TABLE_COLUMNS_ORDER[b] || 1000)
          }).map((key) => {
            return {
              text: formatKeyToLabel(key),
              value: key,
              sortable: false
            }
          })

          this.multipleParcelsTableHeaders = [...headers, { text: 'Action', value: 'action', sortable: false }]

          const proofDocsData = this.scrappedData.filter(item => item.data_type === 'pdf' && this.multipleParcelCaseDocNames.includes(item.content_location))
          this.proofDocuments = proofDocsData.map(item => {
            const { Site_url: siteUrl } = item.json_data || {}

            return {
              ...item,
              name: formatKeyToLabel(item.content_location || 'document'),
              site_url: siteUrl || null
            }
          })
          this.showNoData = !this.loading
        } else {
          this.hasMultipleRecords = false
          this.showNoData = !this.loading && !Object.keys(jsonData.current_parcel || {}).length
        }
      } else {
        this.hasMultipleRecords = false
        this.showNoData = !this.loading && !scrappedDataJson
      }
    },
    handleRetrieveParcel (item) {
      this.$store.dispatch(TRIGGERRUNSHEETSCRAPPING, {
        orderId: this.order.id,
        parcelId: item.apn
      }).then(() => {
        this.refetchScrapperState()
      }).finally(() => {
        this.loadingScrappingData = false
      })
    }
  },
  computed: {
    allowRetrieval: function () {
      const { SUCCESS, FAILED } = SCRAPPER_STATES
      return [SUCCESS, FAILED].includes(this.scrapperState)
    }
  },
  watch: {
    fetchOnActive (newVal) {
      if (newVal) {
        this.getData()
      }
    },
    scrappedData: 'updateNoDataState',
    loading: 'updateNoDataState'
  }
}
</script>

<style scoped>
.container {
  width: 100%;
  height: 100%;
  margin: 0 !important;
  padding: 12px 0 !important;
  max-width: 100% !important;
}

.text-content {
  font-size: 14px;
}

.navigation-btn {
  width: fit-content;
  height: 28px !important;
  border-radius: 4px;
  padding: 4px;
  font-size: 12px;
}

.table-container {
  margin-bottom: 12px;
}

.important-order-details {
  width: 100%;
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.important-order-details span {
  font-size: 16px;
  text-transform: capitalize;
}

.action-column {
  display: flex;
  gap:12px;
}

.custom-v-table {
  margin-bottom: 12px;
  border-radius: 0;
}

.card {
  background: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  padding: 12px;
  margin-bottom: 12px;
}

.card-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}

.custom-v-table >>> table {
  width: 100%;
  margin-top: 12px;
  border-collapse: separate !important;
  border-spacing: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.custom-v-table .custom-header-row {
  background-color: #f9fafb;
}

.custom-v-table >>> th {
  text-align: left;
  padding: 8px 12px;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid #e5e7eb;
}

.custom-v-table tbody tr {
  font-size: 16px;
  transition: background-color 0.2s ease;
}

.custom-v-table tbody tr:hover {
  background-color: #f9fafb;
}

.custom-v-table .custom-data-row td {
  padding: 8px 12px;
  font-size: 14px !important;
  border-bottom: 1px solid #e5e7eb;
}

.custom-v-table tbody tr:last-child td {
  border-bottom: none;
}

.no-result {
  display: flex;
  flex-direction: column;
  gap:16px;
  justify-content: center;
  height: fit-content;
  margin-top: 12px;
  width: 100%;
}

.tabs-container{
  background-color: transparent;
  width: 100%;
}

.no-result-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 20px;
  width: 100%;
  padding: 12px;
  min-height: 60px;
  background-color: white;
}

.no-result-container h4 {
  margin:12px 0 8px 0;
  font-size: 18px;
}

.screenshot-doc {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 2px solid #eee;
  padding:8px;
  border-radius: 8px;
  width: 100%;
}

.screenshot-doc h5 {
  font-size: 14px;
}

.no-result-container :is(h3,h4,h5) {
font-weight: 500;
}

.screenshot-docs-container{
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2,1fr);
  gap:12px;
}

::v-deep .v-window__container{
  width: 100%;
}

.multiple-parcels-container{
  padding: 12px;
  width: 100%;
  background-color: white;
  margin-top: 12px;
}

.retrieved-label{
  font-size: 14px;
  font-weight: 600;
  background-color: #1976d2;
  padding: 4px 8px;
  color: white;
  border-radius: 4px;
}
</style>
